// src/hooks/useProductCategories.ts - Hooks pour la gestion des catégories de produits

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { ProductCategory } from '../types/inventory';
import { useAuth } from '../contexts/AuthContext';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { usePermissions } from './usePermissions';

// ===== HOOKS POUR LES CATÉGORIES =====

export const useProductCategories = () => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'categories', businessId],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        const q = query(
          collection(db, COLLECTIONS.productCategories),
          where('businessId', '==', businessId),
          where('isActive', '==', true),
          orderBy('name', 'asc')
        );

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useProductCategories - Main query');
          throw error;
        });
        
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            name: data.name,
            isActive: data.isActive,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          } as ProductCategory;
        });
        
      } catch (error) {
        console.error('Erreur lors de la récupération des catégories:', error);
        return [];
      }
    },
    enabled: !!businessId && (
      !!permissions?.canViewCategories ||
      !!permissions?.canViewInventoryReports ||
      !!permissions?.canViewProductSales ||
      !!permissions?.canViewCashReports ||
      !!permissions?.canViewDashboard ||
      !!permissions?.canViewAccounting ||
      !!permissions?.canCreateProductSales
    ),
  });
};

// Hook pour obtenir les statistiques des catégories (nombre de produits par catégorie)
export const useProductCategoryStats = () => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['productCategoryStats', businessId],
    queryFn: async () => {
      if (!businessId) {
        return {};
      }

      try {
        const productsQuery = query(
          collection(db, COLLECTIONS.products),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );

        const productsSnap = await getDocs(productsQuery).catch(error => {
          handleFirestoreError(error, 'useProductCategories - Products stats query');
          throw error;
        });

        const stats: Record<string, number> = {};
        productsSnap.docs.forEach(doc => {
          const data = doc.data();
          const categoryId = data.categoryId;
          if (categoryId) {
            stats[categoryId] = (stats[categoryId] || 0) + 1;
          }
        });

        return stats;
      } catch (error) {
        console.error('Erreur lors du calcul des statistiques de catégories produits:', error);
        return {};
      }
    },
    enabled: !!businessId && (!!permissions?.canViewCategories || !!permissions?.canViewInventoryReports),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useAddProductCategory = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (categoryData: { name: string }) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }

      // Vérifier si une catégorie avec ce nom existe déjà
      const existingQuery = query(
        collection(db, COLLECTIONS.productCategories),
        where('businessId', '==', businessId),
        where('name', '==', categoryData.name.trim()),
        where('isActive', '==', true)
      );
      
      const existingSnap = await getDocs(existingQuery).catch(error => {
        handleFirestoreError(error, 'useProductCategories - Existing query');
        throw error;
      });
      if (!existingSnap.empty) {
        throw new Error('Une catégorie avec ce nom existe déjà');
      }

      const newCategory = {
        businessId,
        name: categoryData.name.trim(),
        isActive: true,
        createdAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.productCategories), newCategory);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'categories', businessId] });
    },
  });
};

export const useUpdateProductCategory = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }

      // Vérifier si une autre catégorie avec ce nom existe déjà
      const existingQuery = query(
        collection(db, COLLECTIONS.productCategories),
        where('businessId', '==', businessId),
        where('name', '==', name.trim()),
        where('isActive', '==', true)
      );
      
      const existingSnap = await getDocs(existingQuery).catch(error => {
        handleFirestoreError(error, 'useProductCategories - Existing query');
        throw error;
      });
      const duplicateExists = existingSnap.docs.some(doc => doc.id !== id);
      
      if (duplicateExists) {
        throw new Error('Une catégorie avec ce nom existe déjà');
      }

      const categoryRef = doc(db, COLLECTIONS.productCategories, id);
      await updateDoc(categoryRef, {
        name: name.trim(),
        updatedAt: Timestamp.now(),
      });

      return { id, name: name.trim() };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'categories', businessId] });
    },
  });
};

export const useDeleteProductCategory = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (categoryId: string) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }

      // Vérifier qu'aucun produit n'utilise cette catégorie
      const productsQuery = query(
        collection(db, COLLECTIONS.products),
        where('businessId', '==', businessId),
        where('categoryId', '==', categoryId),
        where('isActive', '==', true)
      );
      
      const productsSnap = await getDocs(productsQuery).catch(error => {
        handleFirestoreError(error, 'useProductCategories - Products query');
        throw error;
      });
      if (!productsSnap.empty) {
        throw new Error(
          `Impossible de supprimer cette catégorie car ${productsSnap.size} produit(s) l'utilisent`
        );
      }

      // Désactiver la catégorie au lieu de la supprimer
      const categoryRef = doc(db, COLLECTIONS.productCategories, categoryId);
      await updateDoc(categoryRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });

      return categoryId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'categories', businessId] });
    },
  });
};

/** Catégories produits d'un business (Super Admin / préparation import), sans filtre permissions « business courant ». */
export const useProductCategoriesForBusinessId = (
  businessId: string | undefined,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled !== false;
  return useQuery({
    queryKey: ['inventory', 'categories', 'forBusiness', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      const q = query(
        collection(db, COLLECTIONS.productCategories),
        where('businessId', '==', businessId),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );

      const snapshot = await getDocs(q).catch((error) => {
        handleFirestoreError(error, 'useProductCategoriesForBusinessId');
        throw error;
      });

      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          businessId: data.businessId,
          name: data.name,
          isActive: data.isActive,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as ProductCategory;
      });
    },
    enabled: !!businessId && enabled,
  });
};

export const useSuperAdminBulkImportProductCategories = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM, userProfile } = useAuth();

  return useMutation({
    mutationFn: async (params: { businessId: string; names: string[] }) => {
      const { businessId, names } = params;

      if (!isSuperAdmin && !(isCSM && (userProfile?.businessIds?.includes(businessId) ?? false))) {
        throw new Error('Accès refusé pour ce business.');
      }
      const trimmed = names.map((n) => n.trim()).filter(Boolean);
      if (!trimmed.length) {
        throw new Error('Aucune catégorie à créer.');
      }

      const existingSnap = await getDocs(
        query(
          collection(db, COLLECTIONS.productCategories),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        )
      ).catch((error) => {
        handleFirestoreError(error, 'useSuperAdminBulkImportProductCategories existing');
        throw error;
      });

      const usedExact = new Set<string>();
      existingSnap.docs.forEach((d) => {
        const n = d.data().name;
        if (typeof n === 'string' && n.trim()) usedExact.add(n.trim());
      });

      const createdIds: string[] = [];

      for (const name of trimmed) {
        if (usedExact.has(name)) {
          throw new Error(`La catégorie « ${name} » existe déjà (ou doublon dans l’import).`);
        }

        const dupQuery = query(
          collection(db, COLLECTIONS.productCategories),
          where('businessId', '==', businessId),
          where('name', '==', name),
          where('isActive', '==', true)
        );
        const dupSnap = await getDocs(dupQuery).catch((error) => {
          handleFirestoreError(error, 'useSuperAdminBulkImportProductCategories dup');
          throw error;
        });
        if (!dupSnap.empty) {
          throw new Error(`La catégorie « ${name} » existe déjà.`);
        }

        const docRef = await addDoc(collection(db, COLLECTIONS.productCategories), {
          businessId,
          name,
          isActive: true,
          createdAt: Timestamp.now(),
        });
        createdIds.push(docRef.id);
        usedExact.add(name);
      }

      return { created: createdIds.length, ids: createdIds };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'categories', 'forBusiness', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'categories', variables.businessId] });
    },
  });
};

/** Désactive toutes les catégories actives sans produit actif associé. Réservé au Super Admin. */
export const useSuperAdminPurgeAllProductCategoriesForBusiness = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (params: { businessId: string }) => {
      if (!isSuperAdmin) {
        throw new Error('Cette action est réservée au Super Admin.');
      }
      const { businessId } = params;

      const catSnap = await getDocs(
        query(
          collection(db, COLLECTIONS.productCategories),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        )
      ).catch((error) => {
        handleFirestoreError(error, 'useSuperAdminPurgeAllProductCategoriesForBusiness list');
        throw error;
      });

      let deactivated = 0;
      let skippedHasProducts = 0;

      for (const d of catSnap.docs) {
        const categoryId = d.id;
        const pqSnap = await getDocs(
          query(
            collection(db, COLLECTIONS.products),
            where('businessId', '==', businessId),
            where('categoryId', '==', categoryId),
            where('isActive', '==', true),
            limit(1)
          )
        ).catch((error) => {
          handleFirestoreError(error, 'useSuperAdminPurgeAllProductCategoriesForBusiness products');
          throw error;
        });
        if (!pqSnap.empty) {
          skippedHasProducts += 1;
          continue;
        }
        await updateDoc(doc(db, COLLECTIONS.productCategories, categoryId), {
          isActive: false,
          updatedAt: Timestamp.now(),
        });
        deactivated += 1;
      }

      return { deactivated, skippedHasProducts, scanned: catSnap.size };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'categories', 'forBusiness', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'categories', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['productCategoryStats', variables.businessId] });
    },
  });
};